import moment from 'moment';
import { Event } from 'react-big-calendar';
import { tasksService } from 'src/data/api/helios-service/tasksService';
import { auditService } from 'src/data/api/helios-service/auditService';
import { formatDateTime } from 'src/utils/generalUtils';
import useUserProfile from './useUserProfile';
import { useEffect, useState, useCallback } from 'react';
import { Audit } from 'src/types/helios/Audit';
import { Task } from 'src/types/helios/Task';
import { DEFAULT_LANGUAGE } from 'src/contstant/commonLabels';
import { AuditStatus } from 'src/contstant/commonLabels';
import { TaskStatus } from '@amzn/gbsa-helios-typescript-client';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import { openTaskDetailsPage } from 'src/helper/taskHelper';
import { getAudiSummaryUrl } from 'src/pages/audits/auditsConfig';
import { STATUS_COLORS, STATUS_BG_COLORS } from 'src/utils/colors';

type EventType = 'Task' | 'Audit';

// Custom interface for calendar events to avoid conflicts with DOM Event interface
interface CalendarEvent {
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  type: EventType;
  status?: string;
  resource?: any;
}

// Status mapping constants
const AUDIT_STATUS_MAP: Record<AuditStatus, keyof typeof STATUS_COLORS> = {
  [AuditStatus.COMPLETED]: 'COMPLETED',
  [AuditStatus.IN_PROGRESS]: 'IN_PROGRESS',
  [AuditStatus.EXPIRED]: 'EXPIRED',
  [AuditStatus.CANCELLED]: 'CANCELLED',
  [AuditStatus.NOT_STARTED]: 'NOT_STARTED',
  [AuditStatus.SCHEDULED]: 'NOT_STARTED',
} as const;

const TASK_STATUS_MAP: Partial<Record<TaskStatus, keyof typeof STATUS_COLORS>> = {
  [TaskStatus.COMPLETED]: 'COMPLETED',
  [TaskStatus.IN_PROGRESS]: 'IN_PROGRESS',
  [TaskStatus.ASSIGNED]: 'ASSIGNED',
  [TaskStatus.OVERDUE]: 'OVERDUE',
  [TaskStatus.CANCELLED]: 'CANCELLED',
} as const;

// Type guards for safer type checking
const isAuditStatus = (status: string): status is AuditStatus =>
  Object.values(AuditStatus).includes(status as AuditStatus);

const isTaskStatus = (status: string): status is TaskStatus =>
  Object.values(TaskStatus).includes(status as TaskStatus);

// Color mapping for audit statuses to match Audit Overview widgets
// Centralized status color mapping using CSS custom properties
// This ensures consistency across all components (Calendar, Overview widgets, etc.)
const getStatusColorStyle = (status: string, type: 'audit' | 'task') => {
  const getColorKey = (
    status: string,
    type: 'audit' | 'task',
  ): keyof typeof STATUS_COLORS => {
    if (type === 'audit' && isAuditStatus(status)) {
      return AUDIT_STATUS_MAP[status] || 'NOT_STARTED';
    } else if (type === 'task' && isTaskStatus(status)) {
      return TASK_STATUS_MAP[status] || 'ASSIGNED';
    }
    return type === 'audit' ? 'NOT_STARTED' : 'ASSIGNED';
  };

  const colorKey = getColorKey(status, type);

  return {
    backgroundColor: STATUS_BG_COLORS[colorKey],
    borderColor: STATUS_COLORS[colorKey],
    color: STATUS_COLORS[colorKey],
  };
};

const getAuditColorByStatus = (status: string) => getStatusColorStyle(status, 'audit');
const getTaskColorByStatus = (status: string) => getStatusColorStyle(status, 'task');

export const useCalendar = () => {
  const { getUserBusinessUnitId, currentUser, getLanguage, getLocations } =
    useUserProfile();
  const navigate = useNavigate();

  const [calendarEvent, setCalendarEvent] = useState<CalendarEvent[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);

  const loadTasks = useCallback(
    async (startDate: string, endDate: string) => {
      try {
        const response = await tasksService.search({
          businessUnitId: getUserBusinessUnitId(),
          language: DEFAULT_LANGUAGE,
          locations: getLocations(),
          dateRange: {
            type: 'CUSTOM',
            startDate,
            endDate,
          },
        });

        return (
          response.tasks
            ?.filter(
              (task: Task) =>
                task.dueDate &&
                task.taskStatus !== TaskStatus.CANCELLED &&
                task.taskStatus !== TaskStatus.DRAFT,
            )
            .map((task: Task) => {
              const startDate = moment(
                formatDateTime(task.createdAt, 'YYYY-MM-DD'),
              ).toDate();
              startDate.setHours(0, 0, 0, 0);
              return {
                title: `Task - ${task.localizedContent?.content.taskName ?? 'Task'}`,
                start: startDate,
                end: moment(formatDateTime(task.dueDate!, 'YYYY-MM-DD')).toDate(),
                allDay: true,
                type: 'Task' as EventType,
                status: task.taskStatus, // Include status for color coding
                resource: task, // Include original task data for navigation
              };
            }) || []
        );
      } catch (error) {
        console.error('Failed to load tasks:', error);
        return [];
      }
    },
    [getUserBusinessUnitId, getLocations],
  );

  const loadAudits = useCallback(
    async (startDate: string, endDate: string) => {
      try {
        const response = await auditService.search({
          businessUnitId: getUserBusinessUnitId(),
          locations: getLocations(),
          dateRange: {
            type: 'CUSTOM',
            startDate,
            endDate,
          },
        });

        return (
          response.audits
            ?.filter((audit: Audit) => audit.status !== AuditStatus.CANCELLED)
            .filter((audit: Audit) => audit.startDate) // Only include audits with startDate
            .map((audit: Audit) => {
              const auditStartTime = audit.startDate;
              const startDate = moment(
                formatDateTime(auditStartTime, 'YYYY-MM-DD'),
              ).toDate();
              startDate.setHours(0, 30, 0, 0);
              return {
                title: `Audit - ${audit.name || '-'}`,
                start: startDate,
                end: audit.expirationDate
                  ? moment(formatDateTime(audit.expirationDate, 'YYYY-MM-DD')).toDate()
                  : moment(startDate).add(1, 'day').toDate(),
                allDay: true,
                type: 'Audit' as EventType,
                status: audit.status, // Include status for color coding
                resource: audit, // Include original audit data for navigation
              };
            }) || []
        );
      } catch (error) {
        console.error('Failed to load audits:', error);
        return [];
      }
    },
    [getUserBusinessUnitId, getLocations],
  );

  const loadMonthData = useCallback(
    async (date: Date) => {
      const startOfMonth =
        moment.utc(date).startOf('month').format('YYYY-MM-DDTHH:mm:ss') + 'Z';
      const endOfMonth =
        moment.utc(date).endOf('month').format('YYYY-MM-DDTHH:mm:ss') + 'Z';

      setIsLoading(true);
      try {
        const [tasks, audits] = await Promise.all([
          loadTasks(startOfMonth, endOfMonth),
          loadAudits(startOfMonth, endOfMonth),
        ]);
        const allEvents = [...tasks, ...audits];
        setCalendarEvent(allEvents);
      } catch (error) {
        console.error('Failed to load calendar data:', error);
        setCalendarEvent([]);
      } finally {
        setIsLoading(false);
      }
    },
    [loadTasks, loadAudits],
  );

  useEffect(() => {
    loadMonthData(currentDate);
  }, [currentUser, getUserBusinessUnitId, getLanguage, currentDate, loadMonthData]);

  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    if (event.type === 'Audit' && event.status) {
      // Use status-based colors for audits
      return {
        style: getAuditColorByStatus(event.status),
      };
    }

    if (event.type === 'Task' && event.status) {
      // Use status-based colors for tasks
      return {
        style: getTaskColorByStatus(event.status),
      };
    }

    // Default colors for tasks and audits without status
    const colors = {
      Task: { backgroundColor: '#d1ddd5', borderColor: '#265985', color: '#4f6c5b' },
      Audit: { backgroundColor: '#9cccd4', borderColor: '#d68910', color: '#124c60' },
    };

    return {
      style: colors[event.type as EventType] || colors.Task,
    };
  }, []);

  const handleNavigate = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      if (event.type === 'Task' && event.resource) {
        // Navigate to task details page
        openTaskDetailsPage(navigate, event.resource);
      } else if (event.type === 'Audit' && event.resource) {
        // Navigate to audit summary page
        const auditUrl = getAudiSummaryUrl(event.resource);
        navigate(auditUrl);
      }
    },
    [navigate],
  );

  return {
    eventStyleGetter,
    calendarEvent,
    isLoading,
    loadTasks,
    loadAudits,
    onNavigate: handleNavigate,
    onSelectEvent: handleEventClick, // Add click handler for calendar events
  };
};
